import React, {useState} from 'react';
import {Box, Typography, Container, Accordion, AccordionSummary, AccordionDetails, alpha, useTheme} from '@mui/material';
import {useTranslation} from "react-i18next";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const FAQSection = () => {
  const {t} = useTranslation();
  const theme = useTheme();
  const [expanded, setExpanded] = useState('panel0');

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const faqs = [
    {
      question: t('How long does it take to set up QRseller?'),
      answer: t('Most businesses complete setup in under 5 minutes. You add your menu items, customize colors, and generate your QR code. That\'s it. No technical knowledge required.'),
    },
    {
      question: t('Do my customers need to download an app?'),
      answer: t('No. Customers simply scan your QR code with their phone camera and order directly from their mobile browser. No app installation needed.'),
    },
    {
      question: t('What payment methods do you support?'),
      answer: t('We support all major Israeli payment methods including credit cards, Apple Pay, Google Pay, and Bit. You can also enable cash payment option.'),
    },
    {
      question: t('Can I update my menu anytime?'),
      answer: t('Yes! Update prices, add new items, or mark items as sold out instantly from your admin panel. Changes appear immediately on customer menus.'),
    },
    {
      question: t('Is there a contract or can I cancel anytime?'),
      answer: t('No contracts. Cancel anytime. We believe in earning your business every month by providing great service, not locking you in.'),
    },
    {
      question: t('Do you offer customer support?'),
      answer: t('Yes! We provide 24/7 support via WhatsApp, email, and phone. Our team responds within minutes during business hours.'),
    },
    {
      question: t('Can I use QRseller for multiple locations?'),
      answer: t('Absolutely. You can manage multiple locations from one account. Each location gets its own menu and QR code.'),
    },
    {
      question: t('What languages does QRseller support?'),
      answer: t('Currently we support Hebrew, English, and Russian. More languages coming soon. Your customers can switch languages with one tap.'),
    },
    {
      question: t('Is my data secure?'),
      answer: t('Yes. We use bank-level encryption and are registered with the Ministry of Justice. Your data is backed up daily and stored securely.'),
    },
    {
      question: t('Can I try QRseller before committing?'),
      answer: t('Yes! We offer a 14-day free trial with full access to all features. No credit card required to start.'),
    },
  ];

  return (
    <Box
      id="faq"
      sx={{
        py: {xs: 8, md: 12},
        bgcolor: 'background.default',
        position: 'relative',
      }}
    >
      <Container maxWidth="md">
        {/* Section Header */}
        <Box sx={{textAlign: 'center', mb: 8}}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              mb: 2,
            }}
          >
            <HelpOutlineIcon
              sx={{
                fontSize: 32,
                color: 'primary.main',
              }}
            />
          </Box>
          <Typography
            variant="overline"
            sx={{
              color: 'primary.main',
              fontWeight: 600,
              letterSpacing: '0.1em',
              fontSize: '0.875rem',
              display: 'block',
            }}
          >
            {t("FAQ")}
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: {xs: '2rem', md: '3rem'},
              fontWeight: 700,
              mt: 1,
              mb: 2,
              color: 'text.primary',
            }}
          >
            {t("Frequently asked questions")}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: '1.125rem',
              color: 'text.secondary',
            }}
          >
            {t("Got questions? We've got answers.")}
          </Typography>
        </Box>

        {/* FAQ Accordions */}
        <Box sx={{mb: 6}}>
          {faqs.map((faq, index) => (
            <Accordion
              key={index}
              expanded={expanded === `panel${index}`}
              onChange={handleChange(`panel${index}`)}
              sx={{
                mb: 2,
                borderRadius: '12px !important',
                border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                boxShadow: 'none',
                '&:before': {
                  display: 'none',
                },
                '&.Mui-expanded': {
                  margin: '0 0 16px 0',
                  borderColor: 'primary.main',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{color: 'primary.main'}} />}
                sx={{
                  py: 2,
                  px: 3,
                  '&.Mui-expanded': {
                    minHeight: 64,
                  },
                  '& .MuiAccordionSummary-content': {
                    my: 1.5,
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: '1.1rem',
                  }}
                >
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  px: 3,
                  pb: 3,
                  pt: 0,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.7,
                  }}
                >
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Still have questions CTA */}
        <Box
          sx={{
            p: 5,
            borderRadius: '16px',
            textAlign: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              mb: 2,
              color: 'text.primary',
            }}
          >
            {t("Still have questions?")}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              mb: 3,
            }}
          >
            {t("Our team is here to help. Reach out anytime via WhatsApp, email, or phone.")}
          </Typography>
          <Box sx={{display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap'}}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{mb: 0.5}}>
                {t("Email")}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: 'primary.main',
                }}
              >
                pixelmegabit@gmail.com
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{mb: 0.5}}>
                {t("Phone")}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: 'primary.main',
                }}
              >
                +972 58-770-5778
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default FAQSection;
